/**
 * The public-header nav data. Single source for the three nav surfaces the owner
 * defined by PURPOSE (IA 2026-07-22), so the link sets can't drift:
 *
 *  1. TOP HEADER (marketing / company) — the horizontal top-bar nav:
 *     Product / Pricing / Solutions / Guides / About us (HEADER_NAV_LINKS).
 *     Carried by the MARKETING surfaces only since 2026-09-07 (see MARKETING /
 *     PLAYER below), for both signed-in and signed-out viewers alike.
 *  2. LEFT HAMBURGER DRAWER (app navigation) — the play row / All clubs / All
 *     players / All tournaments / All ladders: navItems' APP_NAV_ITEMS, the ONE
 *     list, drawn by the drawer, the public sheet and the desktop rail alike.
 *     Reachable on desktop now that the hamburger shows at every width. Home came
 *     off it on 2026-09-07; the play row is named from features/play/copy.ts.
 *  3. AVATAR DROPDOWN (account / manage) — Workspace (club staff) / Admin
 *     (super-admin) / Profile / Sign out. Company no longer lives here.
 *
 * Labels/routes are fixed by docs/ux/2026-07-18-site-relaunch-header-and-nav.md
 * §4. Visual treatment lives in the CSS modules; the grouped drawer + sheet +
 * avatar dropdown render shared primitives (DrawerNavContent / navRows).
 */

import type { StartClubCtaSource } from '@/lib/analytics/startClubCta'

/** Chrome mode a public surface renders in (from resolveChrome). `auth` is the
 *  logo-only minimal chrome for the sign-in / register / token pages. `app` is
 *  the in-app top bar that coexists with the operational sidebar (Wave-1 nav
 *  polish, docs/ux/2026-07-18-nav-top-plus-sidebar.md): outward links + the
 *  account avatar only, no signed-out state, no primary CTA. */
export type PublicMode = 'marketing' | 'player' | 'auth' | 'app'

export interface PublicNavLink {
  label: string
  to: string
}

export interface PublicCta {
  label: string
  /** The phone label (<480px), same destination and same funnel source. Absent =
   *  `label` fits at every width. */
  shortLabel?: string
  to: string
  /** Set only when this CTA is the club-signup entry point, naming the surface
   *  for the funnel. Player mode's "Join free" leaves it undefined so a player
   *  signup never counts as a club-signup click. */
  ctaSource?: StartClubCtaSource
}

export interface PublicModeConfig {
  /** Wordmark target for this mode. */
  brandTo: string
  /** The horizontal top-bar nav for this mode. Marketing carries the company
   *  set; player carries NOTHING (owner, 2026-09-07 — see PLAYER below). May be
   *  empty, and the header draws no `<nav>` at all when it is. */
  navLinks: PublicNavLink[]
  /** The "Sign in" button's target (signed-out). */
  signInTo: string
  /** The single primary CTA, signed-out. */
  signedOutPrimary: PublicCta
}

/** TOP HEADER — the marketing / company links, carried by the horizontal top bar
 *  on the MARKETING surfaces for every viewer, signed-in or out. Single source of
 *  routes + labels: the mobile COMPANY group (navItems' COMPANY_ITEMS) reads this
 *  list rather than restating it, and marketingChrome.test.mjs reads it to name
 *  the routes no player surface may link to. */
export const HEADER_NAV_LINKS: PublicNavLink[] = [
  { label: 'Product', to: '/for-clubs' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Solutions', to: '/solutions' },
  { label: 'Guides', to: '/guides' },
  { label: 'About us', to: '/about' },
]

/** The signed-in primary CTA — shown only to club managers (isAnyClubStaff). */
export const WORKSPACE_CTA: PublicCta = { label: 'Go to workspace', to: '/workspace' }

const MARKETING: PublicModeConfig = {
  brandTo: '/',
  navLinks: HEADER_NAV_LINKS,
  signInTo: '/login',
  signedOutPrimary: {
    label: 'Start your club',
    shortLabel: 'Start a club',
    to: '/workspace',
    ctaSource: 'header',
  },
}

const PLAYER: PublicModeConfig = {
  // The wordmark goes HOME, in both modes (2026-09-07). It used to aim at `/play`,
  // which was the player-side front door until the hub was taken out of service the
  // day before; since then the target has only been a redirect, so the wordmark on
  // /clubs, /tournaments, /ladders and /players bounced through it to land somewhere
  // it could have gone directly. Two surfaces read this — the header wordmark and the
  // mobile sheet's — and both now go to the home page. The modes still differ where
  // it matters: the signed-out CTA is "Join free" here, "Start your club" there.
  brandTo: '/',
  // NO MARKETING BAR ON A PRODUCT PAGE (owner, 2026-09-07): "если гость заходит на
  // страницу клуба, все это страница клуба, не лендинг, маркетинговые страницы
  // только на лендинг theracketcenter.com". The rule is about the SURFACE, not the
  // viewer — a stranger on /clubs is in the product too, so this is empty signed-in
  // and signed-out alike, and the mobile COMPANY group goes with it (the bar is
  // hidden below 1024px, so leaving that group would only move the bar to a phone).
  //
  // NO PLAYER IS STRANDED BY THIS. Navigation still opens from the hamburger, which
  // shows at EVERY width here (PublicHeader.module.css) and carries the app list — five
  // rows since Home came off it (2026-09-07), the same five every other surface draws;
  // the sign-in door and "Join free" are in the cluster to its right, untouched.
  //
  // THE MARKETING PAGES ARE. For part of 2026-09-07 they kept a link from these pages
  // through a slim footer row, and then the owner struck that too — told plainly what
  // it cost, he chose it: these surfaces name no marketing route at any width now, and
  // /pricing, /solutions, /guides and /about are reached from the marketing site.
  navLinks: [],
  signInTo: '/login',
  signedOutPrimary: { label: 'Join free', to: '/register' },
}

export function publicModeConfig(mode: PublicMode): PublicModeConfig {
  return mode === 'player' ? PLAYER : MARKETING
}
